import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  Typography,
  Divider,
  Box
} from '@mui/material';
import { stakeholderFieldOptions } from '../utils/stakeholderService';
import RoleSelector from './RoleSelector';

const StakeholderDialog = ({
  open,
  onClose,
  onSave,
  stakeholder,
  onChange,
  validationErrors = [],
  loading = false,
  isEdit = false
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {isEdit ? 'Edit Stakeholder' : 'Add New Stakeholder'}
      </DialogTitle>
      <DialogContent>
        {validationErrors.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              {validationErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </Alert>
        )}
        
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              name="name"
              label="Full Name *"
              value={stakeholder.name || ''}
              onChange={onChange}
              fullWidth
              variant="outlined"
              margin="dense"
              required
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              name="preferred_name"
              label="Preferred Name"
              value={stakeholder.preferred_name || ''}
              onChange={onChange}
              fullWidth
              variant="outlined"
              margin="dense"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <RoleSelector
              name="role"
              value={stakeholder.role || 'Business Owner'}
              onChange={onChange}
              label="Role"
              fullWidth
              margin="dense"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              name="organization"
              label="Organization/Department"
              value={stakeholder.organization || ''}
              onChange={onChange}
              fullWidth
              variant="outlined"
              margin="dense"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth margin="dense">
              <InputLabel>Domain / Module</InputLabel>
              <Select
                name="domain_module"
                value={stakeholder.domain_module || ''}
                onChange={onChange}
                label="Domain / Module"
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {stakeholderFieldOptions.domainModule.map(opt => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          {/* Project Analysis */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Project Analysis
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <FormControl fullWidth margin="dense">
              <InputLabel>Impact Level</InputLabel>
              <Select
                name="impact"
                value={stakeholder.impact || 'Medium'}
                onChange={onChange}
                label="Impact Level"
              >
                {stakeholderFieldOptions.impact.map(level => (
                  <MenuItem key={level} value={level}>{level}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <FormControl fullWidth margin="dense">
              <InputLabel>Influence Level</InputLabel>
              <Select
                name="influence"
                value={stakeholder.influence || 'Medium'}
                onChange={onChange}
                label="Influence Level"
              >
                {stakeholderFieldOptions.influence.map(level => (
                  <MenuItem key={level} value={level}>{level}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <FormControl fullWidth margin="dense">
              <InputLabel>Sentiment</InputLabel>
              <Select
                name="sentiment"
                value={stakeholder.sentiment || 'Neutral'}
                onChange={onChange}
                label="Sentiment"
              >
                {stakeholderFieldOptions.sentiment.map(sentiment => (
                  <MenuItem key={sentiment} value={sentiment}>{sentiment}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          {/* Contact Information */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Contact Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              name="main_email"
              label="Main Email"
              type="email"
              value={stakeholder.main_email || ''}
              onChange={onChange}
              fullWidth
              variant="outlined"
              margin="dense"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              name="phone_number"
              label="Phone Number"
              value={stakeholder.phone_number || ''}
              onChange={onChange}
              fullWidth
              variant="outlined"
              margin="dense"
            />
          </Grid>
          
          {/* Project Dates */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Project Timeline
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              name="start_date"
              label="Start Date"
              type="date"
              value={stakeholder.start_date || ''}
              onChange={onChange}
              fullWidth
              variant="outlined"
              margin="dense"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              name="end_date"
              label="End Date"
              type="date"
              value={stakeholder.end_date || ''}
              onChange={onChange}
              fullWidth
              variant="outlined"
              margin="dense"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          
          {/* System Access */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              System Access & Setup
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <FormControl fullWidth margin="dense">
              <InputLabel>SAF Submitted</InputLabel>
              <Select
                name="saf_submitted"
                value={stakeholder.saf_submitted || 'N/A'}
                onChange={onChange}
                label="SAF Submitted"
              >
                {stakeholderFieldOptions.safSubmitted.map(option => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <FormControl fullWidth margin="dense">
              <InputLabel>PS Email</InputLabel>
              <Select
                name="ps_email"
                value={stakeholder.ps_email || 'N/A'}
                onChange={onChange}
                label="PS Email"
              >
                {stakeholderFieldOptions.psEmail.map(option => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <FormControl fullWidth margin="dense">
              <InputLabel>VPN Access</InputLabel>
              <Select
                name="vpn"
                value={stakeholder.vpn || 'N/A'}
                onChange={onChange}
                label="VPN Access"
              >
                {stakeholderFieldOptions.vpn.map(option => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <FormControl fullWidth margin="dense">
              <InputLabel>Teams Folder Access</InputLabel>
              <Select
                name="teams_folder_main_team"
                value={stakeholder.teams_folder_main_team || 'N/A'}
                onChange={onChange}
                label="Teams Folder Access"
              >
                {stakeholderFieldOptions.teamsFolder.map(option => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <FormControl fullWidth margin="dense">
              <InputLabel>KIP Access</InputLabel>
              <Select
                name="kip"
                value={stakeholder.kip || 'N/A'}
                onChange={onChange}
                label="KIP Access"
              >
                {stakeholderFieldOptions.kip.map(option => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <FormControl fullWidth margin="dense">
              <InputLabel>Jira Access</InputLabel>
              <Select
                name="jira"
                value={stakeholder.jira || 'N/A'}
                onChange={onChange}
                label="Jira Access"
              >
                {stakeholderFieldOptions.jira.map(option => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          {/* Project Details */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Project Details
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              name="interests"
              label="Key Interests"
              value={stakeholder.interests || ''}
              onChange={onChange}
              fullWidth
              variant="outlined"
              margin="dense"
              multiline
              rows={4}
              placeholder="What are the stakeholder's main interests in the project?"
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              name="concerns"
              label="Key Concerns"
              value={stakeholder.concerns || ''}
              onChange={onChange}
              fullWidth
              variant="outlined"
              margin="dense"
              multiline
              rows={4}
              placeholder="What are the stakeholder's main concerns or issues?"
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              name="engagement"
              label="Engagement Strategy"
              value={stakeholder.engagement || ''}
              onChange={onChange}
              fullWidth
              variant="outlined"
              margin="dense"
              multiline
              rows={4}
              placeholder="How should this stakeholder be engaged during the project?"
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              name="notes"
              label="Additional Notes"
              value={stakeholder.notes || ''}
              onChange={onChange}
              fullWidth
              variant="outlined"
              margin="dense"
              multiline
              rows={4}
              placeholder="Any additional notes about this stakeholder..."
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={onSave} 
          variant="contained" 
          disabled={loading || !stakeholder.name?.trim()}
        >
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StakeholderDialog;